import { useState } from 'react'
import { z } from 'zod'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Switch } from '@/view/components/ui/switch'
import { Button } from '@/view/components/ui/button'
import { Spinner } from '@/view/components/ui/spinner'
import { useToast } from '@/view/components/ui/use-toast'
import { updateConfig } from '@/app/services/configService/updateConfig'
import { ConfigCard } from './ConfigCard'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/view/components/ui/alert-dialog'

const mergedSchema = z.object({
  SHOW_CONTENT: z.boolean(),
  SHOW_PRIZES: z.boolean(),
  SHOW_LINKS: z.boolean(),
  SHOW_PIN: z.boolean(),
  CAN_CREATE_SELLER: z.boolean(),
  CAN_REGISTER: z.boolean(),
  CAN_SEND_NOTIFICATION: z.boolean(),
  SHOW_LEADS_PAGE: z.boolean(),
  SHOW_RADAR_PAGE: z.boolean(),
  SHOW_MANAGER_PAGE: z.boolean(),
})

const items = [
  { key: 'SHOW_CONTENT', label: 'Conteúdos' },
  { key: 'SHOW_PRIZES', label: 'Sorteios' },
  { key: 'SHOW_LINKS', label: 'Links' },
  { key: 'SHOW_PIN', label: 'PIN' },
  { key: 'CAN_CREATE_SELLER', label: 'Criar Vendedor' },
  { key: 'CAN_REGISTER', label: 'Cadastro' },
  { key: 'CAN_SEND_NOTIFICATION', label: 'Enviar Notificação' },
  { key: 'SHOW_LEADS_PAGE', label: 'Página de Leads' },
  { key: 'SHOW_RADAR_PAGE', label: 'Página Radar' },
  { key: 'SHOW_MANAGER_PAGE', label: 'Página de Gerentes' },
]

interface Props {
  configs: Config[]
  onSubmit: () => void
}

export const FeaturesCardWithModal = ({ configs, onSubmit }: Props) => {
  const [open, setOpen] = useState(false)
  const { toast } = useToast()

  const mergedConfig = configs.reduce((acc, cfg) => ({ ...acc, ...cfg }), {} as any)
  const latestUpdatedAt = configs.reduce(
    (latest, cfg) => (cfg.updatedAt > latest ? cfg.updatedAt : latest),
    configs[0]?.updatedAt || '',
  )

  const form = useForm({
    resolver: zodResolver(mergedSchema),
    defaultValues: {
      SHOW_CONTENT: Boolean(mergedConfig.SHOW_CONTENT),
      SHOW_PRIZES: Boolean(mergedConfig.SHOW_PRIZES),
      SHOW_LINKS: Boolean(mergedConfig.SHOW_LINKS),
      SHOW_PIN: Boolean(mergedConfig.SHOW_PIN),
      CAN_CREATE_SELLER: Boolean(mergedConfig.CAN_CREATE_SELLER),
      CAN_REGISTER: Boolean(mergedConfig.CAN_REGISTER),
      CAN_SEND_NOTIFICATION: Boolean(mergedConfig.CAN_SEND_NOTIFICATION),
      SHOW_LEADS_PAGE: Boolean(mergedConfig.SHOW_LEADS_PAGE),
      SHOW_RADAR_PAGE: Boolean(mergedConfig.SHOW_RADAR_PAGE),
      SHOW_MANAGER_PAGE: Boolean(mergedConfig.SHOW_MANAGER_PAGE),
    },
  })

  const handleSubmit = async (formData: any) => {
    try {
      const featuresConfig = configs.find((c) => c.type === 'features')
      const actionsConfig = configs.find((c) => c.type === 'actions')
      const pagesConfig = configs.find((c) => c.type === 'pages')

      await Promise.all([
        featuresConfig &&
          updateConfig(featuresConfig._id, {
            SHOW_CONTENT: formData.SHOW_CONTENT,
            SHOW_PRIZES: formData.SHOW_PRIZES,
            SHOW_LINKS: formData.SHOW_LINKS,
            SHOW_PIN: formData.SHOW_PIN,
            type: 'features',
          }),
        actionsConfig &&
          updateConfig(actionsConfig._id, {
            CAN_CREATE_SELLER: formData.CAN_CREATE_SELLER,
            CAN_REGISTER: formData.CAN_REGISTER,
            CAN_SEND_NOTIFICATION: formData.CAN_SEND_NOTIFICATION,
            type: 'actions',
          }),
        pagesConfig &&
          updateConfig(pagesConfig._id, {
            SHOW_LEADS_PAGE: formData.SHOW_LEADS_PAGE,
            SHOW_RADAR_PAGE: formData.SHOW_RADAR_PAGE,
            SHOW_MANAGER_PAGE: formData.SHOW_MANAGER_PAGE,
            type: 'pages',
          }),
      ])

      onSubmit()
      toast({ title: 'Configuração atualizada com sucesso' })
      setOpen(false)
    } catch {
      toast({ title: 'Não foi possível atualizar a configuração' })
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <div className="flex h-full w-full items-center justify-between">
          <ConfigCard
            updatedAt={latestUpdatedAt}
            title="Funcionalidades"
            items={items.slice(0, 3).map((item) => ({
              label: item.label,
              value: Boolean(mergedConfig[item.key]),
              isSwitch: true,
              showSwitch: true,
            }))}
          />
        </div>
      </AlertDialogTrigger>

      <AlertDialogContent className="sm:max-w-md">
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <FormProvider {...form}>
            <AlertDialogHeader className="border-b pb-4">
              <AlertDialogTitle className="text-xl font-semibold text-black01">
                Funcionalidades
              </AlertDialogTitle>
            </AlertDialogHeader>

            <AlertDialogDescription className="flex flex-col gap-4 py-4 text-base text-black04">
              {items.map((item) => (
                <div key={item.key} className="flex items-center justify-between">
                  <label className="text-sm font-medium">{item.label}</label>
                  <Switch
                    checked={form.watch(item.key as any)}
                    onCheckedChange={(checked) =>
                      form.setValue(item.key as any, checked)
                    }
                    className="data-[state=checked]:bg-primary"
                  />
                </div>
              ))}
            </AlertDialogDescription>

            <AlertDialogFooter>
              <AlertDialogCancel asChild>
                <Button
                  variant="outline"
                  className="bg-gray02 text-primary hover:bg-gray01"
                  type="button"
                >
                  Cancelar
                </Button>
              </AlertDialogCancel>
              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="text-white"
              >
                {form.formState.isSubmitting ? (
                  <Spinner className="text-white" />
                ) : (
                  'Confirmar'
                )}
              </Button>
            </AlertDialogFooter>
          </FormProvider>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
